import { AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import type { BuyerSmartImportBatchDto } from "@/api/buyerSmartImport";
import { Button, StatusBadge, Timestamp } from "@/design-system/components/ui";
import { formatNumber } from "@/design-system/lib/format";
import { smartImportCopy } from "./smartImportCopy";
import { batchStatusTone } from "./smartImportStatus";

/**
 * What the queued import is doing (D20).
 *
 * <p>🔴 <b>The counts here are the SERVER's, not the preview's.</b> A queued
 * batch carries the preview's numbers and a finished one carries what actually
 * happened, and they are the same numbers only when nothing moved between the
 * dry-run and the job — which is exactly the thing a reader is checking. Taking
 * them off the batch means the completion screen reports the import rather than
 * re-reporting the estimate.</p>
 */

export interface SmartImportProgressProps {
  batch: BuyerSmartImportBatchDto;
  /** Start again with another file. */
  onStartOver?: () => void;
}

export function SmartImportProgress({ batch, onStartOver }: SmartImportProgressProps) {
  const running = batch.status === "Queued" || batch.status === "Running";

  return (
    <section
      aria-label="Import progress"
      className="grid gap-space-3"
      data-testid="smart-import-progress"
    >
      <div className="flex flex-wrap items-center gap-space-2">
        {running ? (
          <Loader2
            size={16}
            strokeWidth={1.5}
            aria-hidden
            className="animate-spin text-gold-shadow"
          />
        ) : batch.status === "Completed" ? (
          <CheckCircle2 size={16} strokeWidth={1.5} aria-hidden className="text-status-success" />
        ) : (
          <AlertTriangle size={16} strokeWidth={1.5} aria-hidden className="text-status-error" />
        )}
        <span className="font-medium text-onyx" data-testid="smart-import-progress-status">
          {statusSentence(batch.status)}
        </span>
        <StatusBadge tone={batchStatusTone(batch.status)}>{batch.status}</StatusBadge>
        <span className="text-caption text-gray-mid break-all">{batch.fileName}</span>
      </div>

      {batch.status === "Failed" && batch.failureReason && (
        <p
          role="alert"
          className="text-small text-status-error"
          data-testid="smart-import-progress-failure"
        >
          {batch.failureReason}
        </p>
      )}

      {batch.status === "Completed" && (
        <p className="text-small text-onyx" data-testid="smart-import-progress-counts">
          {formatNumber(batch.counts.created)} created ·{" "}
          {formatNumber(batch.counts.merged)} merged ·{" "}
          {formatNumber(batch.counts.alreadyImported)} already imported ·{" "}
          {formatNumber(batch.counts.beforeCutoff)} before the cutoff ·{" "}
          {formatNumber(batch.counts.rejected)} rejected
        </p>
      )}

      {batch.finishedAtUtc && (
        <p className="text-caption text-gray-mid">
          Finished <Timestamp value={batch.finishedAtUtc} />
        </p>
      )}

      <div className="flex flex-wrap gap-space-2">
        {!running && onStartOver && (
          <Button
            variant="text"
            size="sm"
            data-testid="smart-import-start-over"
            onClick={onStartOver}
          >
            Import another file
          </Button>
        )}
      </div>
    </section>
  );
}

function statusSentence(status: BuyerSmartImportBatchDto["status"]): string {
  switch (status) {
    case "Queued":
      return smartImportCopy.queued;
    case "Running":
      return smartImportCopy.running;
    case "Completed":
      return smartImportCopy.completed;
    case "Undone":
      return smartImportCopy.undoneLabel;
    default:
      return smartImportCopy.failed;
  }
}
